/**
 * @file Avatar 头像
 * @description 图片头像，失败或无图时显示文字回退。
 *
 * @whenToUse 用户列表、顶栏用户入口。
 * @whenNotToUse 通用图标按钮（用 Button + lucide）。
 */

import { useState, type HTMLAttributes } from 'react'
import { cn } from '@/lib/cn.ts'

export type AvatarProps = HTMLAttributes<HTMLDivElement> & {
  /** 图片地址 */
  src?: string
  /** 无障碍 / 回退文字来源 */
  alt?: string
  /** 回退显示的 1～2 字 */
  fallback?: string
}

export function Avatar({
  src,
  alt = '',
  fallback,
  className,
  ...rest
}: AvatarProps) {
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(src) && !failed
  const text =
    fallback ??
    (alt.trim() ? alt.trim().slice(0, 2).toUpperCase() : '?')

  return (
    <div
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-surface text-xs font-medium text-foreground',
        className,
      )}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden>{text}</span>
      )}
    </div>
  )
}
